import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, apiPost } from "@/contexts/AuthContext";
import { Eye, EyeOff, ArrowRight, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { user, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    const result = await register(name, email, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleEnroll = async () => {
    setCheckoutLoading(true);
    setError("");
    try {
      const originUrl = window.location.origin;
      const { data } = await apiPost(`${API}/checkout/create`, { origin_url: originUrl });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else setError("Could not start checkout. Please try again.");
    }
    setCheckoutLoading(false);
  };

  // If user is logged in but not enrolled, show payment step
  if (user && user !== false && !user.enrolled) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#F7F5F0' }}>
        <div className="w-full max-w-md">
          <Link to="/" className="block text-center mb-10" data-testid="register-logo-link">
            <span className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>sober-curious.com</span>
          </Link>
          <div className="earthy-card text-center">
            <div className="w-14 h-14 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(74,93,78,0.1)' }}>
              <Lock size={24} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            </div>
            <h1 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Complete Your Enrollment</h1>
            <p className="text-sm mb-2" style={{ color: '#5B665E' }}>Hi {user.name}, you're one step away from starting Causes &amp; Conditions.</p>
            <p className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>$149</p>
            <p className="text-sm mb-8" style={{ color: '#5B665E' }}>
              4 weeks of psychoeducation, practical tools, community support, and a personal regulation toolkit.
            </p>
            {error && (
              <div className="mb-4 p-3 rounded-md text-sm" style={{ background: 'rgba(150,69,69,0.08)', color: '#964545', border: '1px solid rgba(150,69,69,0.2)' }} data-testid="checkout-error">
                {error}
              </div>
            )}
            <button onClick={handleEnroll} disabled={checkoutLoading} className="earthy-btn earthy-btn-primary w-full text-base flex items-center justify-center gap-2" data-testid="enroll-pay-btn">
              {checkoutLoading ? "Redirecting to checkout..." : <>Proceed to Payment <ArrowRight size={18} strokeWidth={1.5} /></>}
            </button>
            <p className="mt-4 text-xs" style={{ color: '#5B665E' }}>Secure checkout powered by Stripe</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#F7F5F0' }}>
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-10" data-testid="register-logo-link">
          <span className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>sober-curious.com</span>
        </Link>
        <div className="earthy-card">
          <h1 className="text-3xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Join the Program</h1>
          <p className="text-sm mb-8" style={{ color: '#5B665E' }}>Create your account, then complete enrollment.</p>
          {error && (
            <div className="mb-4 p-3 rounded-md text-sm" style={{ background: 'rgba(150,69,69,0.08)', color: '#964545', border: '1px solid rgba(150,69,69,0.2)' }} data-testid="register-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#5B665E' }}>Your Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="earthy-input" placeholder="What should we call you?" required data-testid="register-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#5B665E' }}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="earthy-input" placeholder="you@email.com" required data-testid="register-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#5B665E' }}>Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="earthy-input pr-12" placeholder="Min 6 characters" required data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 transition-opacity" data-testid="register-toggle-pw">
                  {showPw ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="earthy-btn earthy-btn-primary w-full text-base" data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Create Account & Enroll"}
            </button>
          </form>
          <p className="text-center mt-8 text-sm" style={{ color: '#5B665E' }}>
            Already enrolled? <Link to="/login" className="font-medium underline" style={{ color: '#4A5D4E' }} data-testid="register-login-link">Log in</Link>
          </p>
        </div>
        <p className="text-center mt-4 text-xs" style={{ color: '#5B665E' }}>
          This is a psychoeducational program, not therapy or medical advice.
        </p>
      </div>
    </div>
  );
}
